import omit from 'lodash/omit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  getStartOfWeek,
  monthIdString,
  parseDateFromISO8601,
  stringifyDateToISO8601,
} from '../../util/dates';
import { uniqueBy } from '../../util/generators';
import { storableError } from '../../util/errors';
import { executeFileUpload } from '../../util/fileHelpers';
import * as log from '../../util/log';
import { notifyAdminOnEditSubmitted, submitListingEdit } from '../../util/api';
import { parse } from '../../util/urlHelpers';
import { isUserAuthorized } from '../../util/userHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

// File verification polling (digital downloads).
// The API verifies uploaded files asynchronously, so the client polls until the
// file reaches 'available'. Delay doubles each attempt, capped at one minute.
const POLL_MAX_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 1000;
const MINUTE_IN_MS = 60 * 1000;
const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
const getNextPollingDelay = delayMs => {
  return delayMs * 2 > MINUTE_IN_MS ? MINUTE_IN_MS : delayMs * 2;
};

// Create array of N items where indexing starts from 1
const getArrayOfNItems = n =>
  Array(n)
    .fill()
    .map((v, i) => i + 1)
    .slice(1);

// Return an array of image ids
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map(img => img.id.uuid);

  // Uploaded images (which are propably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter(unattachedImg =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );
  return duplicateImageEntities.length > 0
    ? {
        uploadedImages: {},
        uploadedImagesOrder: [],
      }
    : {
        uploadedImages,
        uploadedImagesOrder,
      };
};

// After listing creation and update, refresh fileUploads to only what the listing has attached.
// Without this, redux keeps the pre-save upload entries, the panel still considers them
// "unattached", and the navigation guard fires even though the save succeeded.
const updateUploadedFilesState = (existingFileUploads, payload) => {
  const included = payload?.included || [];

  const listingFileAttachmentRefs =
    payload?.data?.relationships?.protectedFileAttachments?.data || [];
  const listingFileAttachments = listingFileAttachmentRefs
    .map(ref => included.find(item => item.id.uuid === ref.id.uuid))
    .filter(item => item && !item.attributes?.deleted);
  const listingFiles = included.filter(item => item.type === 'file');

  const syncedFileUploads = {};

  // Sync files in redux state with those attached to the listing
  listingFileAttachments.forEach(fileAttachment => {
    const id = fileAttachment.id.uuid;
    const fileId = fileAttachment.relationships?.file?.data?.id?.uuid;

    // State entries are keyed by a client-generated tempId at upload time,
    // before the attachment UUID is known, so match via the file's own UUID instead
    const existingEntry = Object.values(existingFileUploads).find(f => f.file?.id?.uuid === fileId);

    if (existingEntry) {
      syncedFileUploads[existingEntry.tempId] = existingEntry;
    } else {
      const file = listingFiles.find(f => f.id.uuid === fileId);
      syncedFileUploads[id] = {
        uploadInProgress: false,
        verificationInProgress: false,
        error: null,
        file,
        sourceFile: null,
        progress: null,
        verificationStatus: file?.attributes?.state,
        tempId: id,
      };
    }
  });

  // Append entries that are in-progress or have errors, not yet attached to the listing
  Object.entries(existingFileUploads).forEach(([key, entry]) => {
    const inProgressEntries = entry.uploadInProgress || entry.verificationInProgress || entry.error;
    if (inProgressEntries && !syncedFileUploads[key]) {
      syncedFileUploads[key] = entry;
    }
  });

  return syncedFileUploads;
};

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

// When navigating through weekly calendar,
// we want to merge new week-related data (inProgres, error) to weeklyExceptionQueries hashmap.
const mergeToWeeklyExceptionQueries = (weeklyExceptionQueries, weekStartId, newDataProps) => {
  return weekStartId
    ? {
        weeklyExceptionQueries: {
          ...weeklyExceptionQueries,
          [weekStartId]: {
            ...weeklyExceptionQueries[weekStartId],
            ...newDataProps,
          },
        },
      }
    : {};
};
// When navigating through monthly calendar (e.g. when adding a new AvailabilityException),
// we want to merge new month-related data (inProgres, error) to monthlyExceptionQueries hashmap.
const mergeToMonthlyExceptionQueries = (monthlyExceptionQueries, monthId, newDataProps) => {
  return monthId
    ? {
        monthlyExceptionQueries: {
          ...monthlyExceptionQueries,
          [monthId]: {
            ...monthlyExceptionQueries[monthId],
            ...newDataProps,
          },
        },
      }
    : {};
};

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => payload => ({ type: actionType, payload, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditListingPage/CLEAR_UPDATED_TAB';
export const CLEAR_PUBLISH_ERROR = 'app/EditListingPage/CLEAR_PUBLISH_ERROR';

export const CREATE_LISTING_DRAFT_REQUEST = 'app/EditListingPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/EditListingPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/EditListingPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/EditListingPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/EditListingPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/EditListingPage/PUBLISH_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditListingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditListingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/EditListingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/EditListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditListingPage/SHOW_LISTINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';
export const FETCH_EXTRA_EXCEPTIONS_SUCCESS =
  'app/EditListingPage/FETCH_EXTRA_AVAILABILITY_EXCEPTIONS_SUCCESS';

export const ADD_EXCEPTION_REQUEST = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_REQUEST';
export const ADD_EXCEPTION_SUCCESS = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_SUCCESS';
export const ADD_EXCEPTION_ERROR = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const SET_STOCK_REQUEST = 'app/EditListingPage/SET_STOCK_REQUEST';
export const SET_STOCK_SUCCESS = 'app/EditListingPage/SET_STOCK_SUCCESS';
export const SET_STOCK_ERROR = 'app/EditListingPage/SET_STOCK_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingPage/UPLOAD_IMAGE_ERROR';

export const REMOVE_LISTING_IMAGE = 'app/EditListingPage/REMOVE_LISTING_IMAGE';

// Digital downloads (default-download process): file upload / verification / download.
// Note: uploads are tracked by a client-side `tempId` because the API-side file id
// only exists after step 2 of the multi-step upload.
export const UPLOAD_FILE_REQUEST = 'app/EditListingPage/UPLOAD_FILE_REQUEST';
export const UPLOAD_FILE_SUCCESS = 'app/EditListingPage/UPLOAD_FILE_SUCCESS';
export const UPLOAD_FILE_ERROR = 'app/EditListingPage/UPLOAD_FILE_ERROR';

export const SET_FILE_UPLOAD_PROGRESS = 'app/EditListingPage/SET_FILE_UPLOAD_PROGRESS';
export const SET_FILE_VERIFICATION_STATUS = 'app/EditListingPage/SET_FILE_VERIFICATION_STATUS';

export const POLL_FILE_VERIFICATION_REQUEST = 'app/EditListingPage/POLL_FILE_VERIFICATION_REQUEST';
export const POLL_FILE_VERIFICATION_SUCCESS = 'app/EditListingPage/POLL_FILE_VERIFICATION_SUCCESS';
export const POLL_FILE_VERIFICATION_ERROR = 'app/EditListingPage/POLL_FILE_VERIFICATION_ERROR';

export const DOWNLOAD_FILE_REQUEST = 'app/EditListingPage/DOWNLOAD_FILE_REQUEST';
export const DOWNLOAD_FILE_SUCCESS = 'app/EditListingPage/DOWNLOAD_FILE_SUCCESS';
export const DOWNLOAD_FILE_ERROR = 'app/EditListingPage/DOWNLOAD_FILE_ERROR';

export const CLEAR_UPLOADED_FILES = 'app/EditListingPage/CLEAR_UPLOADED_FILES';
export const SET_FILE_UPLOADS_DISABLED = 'app/EditListingPage/SET_FILE_UPLOADS_DISABLED';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_ERROR';

export const SAVE_DRAFT_LISTING_REQUEST = 'app/EditListingPage/SAVE_DRAFT_LISTING_REQUEST';
export const SAVE_DRAFT_LISTING_SUCCESS = 'app/EditListingPage/SAVE_DRAFT_LISTING_SUCCESS';
export const SAVE_DRAFT_LISTING_ERROR = 'app/EditListingPage/SAVE_DRAFT_LISTING_ERROR';
export const CLEAR_SAVE_DRAFT_LISTING = 'app/EditListingPage/CLEAR_SAVE_DRAFT_LISTING';

export const SHOW_PENDING_APPROVAL_NOTIFICATION = 'app/EditListingPage/SHOW_PENDING_APPROVAL_NOTIFICATION';
export const CLEAR_PENDING_APPROVAL_NOTIFICATION = 'app/EditListingPage/CLEAR_PENDING_APPROVAL_NOTIFICATION';

// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  listingId: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  setStockError: null,
  setStockInProgress: false,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadedImagesOrder: [],
  removedImageIds: [],
  // Digital downloads: keyed by client-side tempId
  fileUploads: {},
  fileUploadsDisabled: false,
  fileDownloads: {},
  addExceptionError: null,
  addExceptionInProgress: false,
  weeklyExceptionQueries: {
    // '2022-12-12': { // Note: id/key is the start of the week in given time zone
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  monthlyExceptionQueries: {
    // '2022-12': {
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  allExceptions: [],
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  saveDraftListingInprogress: false,
  saveDraftListingError: null,
  saveDraftListingResult: null,
  showPendingApprovalNotification: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null, showPendingApprovalNotification: false };
    case CLEAR_PUBLISH_ERROR:
      return { ...state, publishListingError: null };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };

    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        listingId: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishListingError: {
          listingId: state.listingId,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        // Reconcile pending uploads against what the listing now actually has attached,
        // so the "unsaved file uploads" navigation guard clears after a successful save.
        fileUploads: updateUploadedFilesState(state.fileUploads, payload),
        updateInProgress: false,
        // availabilityCalendar: { ...state.availabilityCalendar },
      };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS: {
      const listingIdFromPayload = payload.data.id;
      const {
        listingId,
        allExceptions,
        weeklyExceptionQueries,
        monthlyExceptionQueries,
        fileUploads,
      } = state;
      // Seed fileUploads from the listing's own attachments. This reducer resets to
      // initialState, so without this the Files panel shows nothing after a reload
      // even though the files are attached to the listing.
      // If listing stays the same, we trust previously fetched exception data.
      return listingIdFromPayload?.uuid === state.listingId?.uuid
        ? {
            ...initialState,
            listingId,
            allExceptions,
            weeklyExceptionQueries,
            monthlyExceptionQueries,
            fileUploads: updateUploadedFilesState(fileUploads, payload),
          }
        : {
            ...initialState,
            listingId: listingIdFromPayload,
            fileUploads: updateUploadedFilesState({}, payload),
          };
    }
    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case FETCH_EXCEPTIONS_REQUEST: {
      const { monthId, weekStartId } = payload.params;
      const newData = { fetchExceptionsError: null, fetchExceptionsInProgress: true };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
        ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
        : {};
      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_SUCCESS: {
      const { exceptions, monthId, weekStartId } = payload;
      const combinedExceptions = state.allExceptions.concat(exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      const newData = { fetchExceptionsInProgress: false };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
        ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
        : {};
      return { ...state, allExceptions, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_ERROR: {
      const { monthId, weekStartId, error } = payload;
      const newData = { fetchExceptionsInProgress: false, fetchExceptionsError: error };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
        ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
        : {};

      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXTRA_EXCEPTIONS_SUCCESS: {
      const combinedExceptions = state.allExceptions.concat(payload.exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      // TODO: currently we don't handle thrown errors from these paginated calls
      return { ...state, allExceptions };
    }
    case ADD_EXCEPTION_REQUEST:
      return {
        ...state,
        addExceptionError: null,
        addExceptionInProgress: true,
      };
    case ADD_EXCEPTION_SUCCESS: {
      const exception = payload;
      const combinedExceptions = state.allExceptions.concat(exception);
      const allExceptions = combinedExceptions.sort(sortExceptionsByStartTime);
      return {
        ...state,
        allExceptions,
        addExceptionInProgress: false,
      };
    }
    case ADD_EXCEPTION_ERROR:
      return {
        ...state,
        addExceptionError: payload.error,
        addExceptionInProgress: false,
      };

    case DELETE_EXCEPTION_REQUEST:
      return {
        ...state,
        deleteExceptionError: null,
        deleteExceptionInProgress: true,
      };
    case DELETE_EXCEPTION_SUCCESS: {
      const exception = payload;
      const id = exception.id.uuid;
      const allExceptions = state.allExceptions.filter(e => e.id.uuid !== id);
      return {
        ...state,
        allExceptions,
        deleteExceptionInProgress: false,
      };
    }
    case DELETE_EXCEPTION_ERROR:
      return {
        ...state,
        deleteExceptionError: payload.error,
        deleteExceptionInProgress: false,
      };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedImages = {
        ...state.uploadedImages,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedImages,
        uploadedImagesOrder: state.uploadedImagesOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, ...rest } = payload;
      const uploadedImages = { ...state.uploadedImages, [id]: { id, ...rest } };
      return { ...state, uploadedImages };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
      const uploadedImages = omit(state.uploadedImages, id);
      return { ...state, uploadedImagesOrder, uploadedImages, uploadImageError: error };
    }

    case UPLOAD_FILE_REQUEST: {
      // payload.params: { tempId, file }
      const { tempId, file } = payload.params;
      return {
        ...state,
        fileUploads: {
          ...state.fileUploads,
          [tempId]: {
            tempId,
            uploadInProgress: true,
            error: null,
            file: null,
            // sourceFile keeps the browser File around so the UI can show a name
            // before the API-side file resource exists.
            sourceFile: file,
            progress: null,
            verificationStatus: null,
          },
        },
      };
    }
    case UPLOAD_FILE_SUCCESS: {
      const { tempId, fileUpload } = payload;
      const existing = state.fileUploads[tempId];
      if (!existing) {
        return state;
      }
      return {
        ...state,
        fileUploads: {
          ...state.fileUploads,
          [tempId]: { ...existing, file: fileUpload, sourceFile: null },
        },
      };
    }
    case UPLOAD_FILE_ERROR: {
      const { tempId, error, file } = payload;
      // A 403 with this code means the marketplace has file uploads switched off
      // entirely - drop every pending upload rather than showing per-file errors.
      const isFileUploadDisabledError =
        error?.status === 403 && error?.apiErrors?.some(ae => ae.code === 'file-upload-disabled');

      if (isFileUploadDisabledError) {
        return { ...state, fileUploadsDisabled: true, fileUploads: {} };
      }
      return {
        ...state,
        fileUploads: {
          ...state.fileUploads,
          [tempId]: { tempId, uploadInProgress: false, error, file: null, sourceFile: file },
        },
      };
    }

    case SET_FILE_UPLOAD_PROGRESS: {
      const { tempId, progress } = payload;
      const existing = state.fileUploads[tempId];
      if (!existing) {
        return state;
      }
      return { ...state, fileUploads: { ...state.fileUploads, [tempId]: { ...existing, progress } } };
    }
    case SET_FILE_VERIFICATION_STATUS: {
      const { tempId, verificationStatus } = payload;
      const existing = state.fileUploads[tempId];
      if (!existing) {
        return state;
      }
      return {
        ...state,
        fileUploads: {
          ...state.fileUploads,
          [tempId]: {
            ...existing,
            verificationStatus,
            uploadInProgress: verificationStatus === 'pendingUpload',
            verificationInProgress: verificationStatus === 'pendingVerification',
          },
        },
      };
    }

    case POLL_FILE_VERIFICATION_REQUEST: {
      const { tempId } = payload.params;
      const existing = state.fileUploads[tempId];
      if (!existing) {
        return state;
      }
      return {
        ...state,
        fileUploads: {
          ...state.fileUploads,
          [tempId]: { ...existing, verificationInProgress: true },
        },
      };
    }
    case POLL_FILE_VERIFICATION_SUCCESS: {
      const { tempId, fileUpload } = payload;
      const existing = state.fileUploads[tempId];
      if (!existing) {
        return state;
      }
      return {
        ...state,
        fileUploads: {
          ...state.fileUploads,
          [tempId]: { ...existing, verificationInProgress: false, file: fileUpload },
        },
      };
    }
    case POLL_FILE_VERIFICATION_ERROR: {
      const { tempId, message, fileUpload } = payload;
      const existing = state.fileUploads[tempId];
      if (!existing) {
        return state;
      }
      return {
        ...state,
        fileUploads: {
          ...state.fileUploads,
          [tempId]: {
            ...existing,
            verificationInProgress: false,
            error: { message },
            ...(fileUpload ? { file: fileUpload } : {}),
          },
        },
      };
    }

    case DOWNLOAD_FILE_REQUEST: {
      const { fileAttachmentId } = payload.params;
      return {
        ...state,
        fileDownloads: {
          ...state.fileDownloads,
          [fileAttachmentId.uuid]: { inProgress: true, error: null },
        },
      };
    }
    case DOWNLOAD_FILE_SUCCESS: {
      const { fileAttachmentId } = payload;
      return {
        ...state,
        fileDownloads: {
          ...state.fileDownloads,
          [fileAttachmentId.uuid]: { inProgress: false, error: null },
        },
      };
    }
    case DOWNLOAD_FILE_ERROR: {
      const { fileAttachmentId, error } = payload;
      return {
        ...state,
        fileDownloads: {
          ...state.fileDownloads,
          [fileAttachmentId.uuid]: { inProgress: false, error },
        },
      };
    }

    case CLEAR_UPLOADED_FILES: {
      const tempIds = payload;
      return { ...state, fileUploads: omit(state.fileUploads, tempIds) };
    }
    case SET_FILE_UPLOADS_DISABLED: {
      return { ...state, fileUploadsDisabled: true };
    }

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);

      return { ...state, uploadedImages, uploadedImagesOrder, removedImageIds };
    }

    case SET_STOCK_REQUEST:
      return { ...state, setStockInProgress: true, setStockError: null };
    case SET_STOCK_SUCCESS:
      return { ...state, setStockInProgress: false };
    case SET_STOCK_ERROR:
      return { ...state, setStockInProgress: false, setStockError: payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };
    case SAVE_DRAFT_LISTING_REQUEST:
      return {
        ...state,
        saveDraftListingInprogress: true,
      };
    case SAVE_DRAFT_LISTING_ERROR:
      return {
        ...state,
        saveDraftListingError: payload,
        saveDraftListingInprogress: false,
      };
    case SAVE_DRAFT_LISTING_SUCCESS:
      return {
        ...state,
        saveDraftListingResult: payload,
        saveDraftListingInprogress: false,
      };
    case CLEAR_SAVE_DRAFT_LISTING:
      return {
        ...state,
        saveDraftListingInprogress: false,
        saveDraftListingError: null,
        saveDraftListingResult: null,
      };

    case SHOW_PENDING_APPROVAL_NOTIFICATION:
      return {
        ...state,
        showPendingApprovalNotification: true,
      };

    case CLEAR_PENDING_APPROVAL_NOTIFICATION:
      return {
        ...state,
        showPendingApprovalNotification: false,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// Note: these selectors default fileUploads to {} because tests and SSR can hand
// in a partial EditListingPage state that predates this key.

// Returns the fileUploads entries as an array, one item per selected file.
export const selectFileUploads = state => Object.values(state.EditListingPage.fileUploads || {});

// Returns true if file uploads are disabled for this marketplace.
export const selectFileUploadsDisabled = state => !!state.EditListingPage.fileUploadsDisabled;

// Returns true if any file is still uploading.
export const selectHasPendingFileUploads = state =>
  Object.values(state.EditListingPage.fileUploads || {}).some(f => f.uploadInProgress);

// Returns true if at least one file exists and all files are fully uploaded and verified.
// This gates publishing: a listing must not go live with a half-uploaded or
// unverified file attached.
export const selectAllFilesUploadedAndVerified = state => {
  const files = Object.values(state.EditListingPage.fileUploads || {});
  return (
    files.length > 0 &&
    files.every(
      f =>
        f.verificationStatus === 'available' &&
        f.file != null &&
        !f.uploadInProgress &&
        !f.verificationInProgress
    )
  );
};

// ================ Action creators ================ //

export const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const removeListingImage = imageId => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

export const clearPublishError = () => ({
  type: CLEAR_PUBLISH_ERROR,
});

// ================ Digital download file actions ================ //

export const uploadFileRequest = requestAction(UPLOAD_FILE_REQUEST);
export const uploadFileSuccess = payload => ({ type: UPLOAD_FILE_SUCCESS, payload });
export const uploadFileError = errorAction(UPLOAD_FILE_ERROR);

export const pollFileVerificationRequest = requestAction(POLL_FILE_VERIFICATION_REQUEST);
export const pollFileVerificationSuccess = payload => ({
  type: POLL_FILE_VERIFICATION_SUCCESS,
  payload,
});
export const pollFileVerificationError = errorAction(POLL_FILE_VERIFICATION_ERROR);

export const downloadFileRequest = requestAction(DOWNLOAD_FILE_REQUEST);
export const downloadFileSuccess = payload => ({ type: DOWNLOAD_FILE_SUCCESS, payload });
export const downloadFileError = errorAction(DOWNLOAD_FILE_ERROR);

export const setUploadProgress = payload => ({ type: SET_FILE_UPLOAD_PROGRESS, payload });
export const setVerificationStatus = payload => ({
  type: SET_FILE_VERIFICATION_STATUS,
  payload,
});
export const clearUploadedFiles = tempIds => ({ type: CLEAR_UPLOADED_FILES, payload: tempIds });
export const setFileUploadsDisabled = () => ({ type: SET_FILE_UPLOADS_DISABLED });

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraftRequest = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListingRequest = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListingRequest = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListingsRequest = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

// SDK method: stock.compareAndSet
export const setStockRequest = requestAction(SET_STOCK_REQUEST);
export const setStockSuccess = successAction(SET_STOCK_SUCCESS);
export const setStockError = errorAction(SET_STOCK_ERROR);

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = requestAction(FETCH_EXCEPTIONS_REQUEST);
export const fetchAvailabilityExceptionsSuccess = successAction(FETCH_EXCEPTIONS_SUCCESS);
export const fetchAvailabilityExceptionsError = errorAction(FETCH_EXCEPTIONS_ERROR);
// Add extra data from additional pages
export const fetchExtraAvailabilityExceptionsSuccess = successAction(
  FETCH_EXTRA_EXCEPTIONS_SUCCESS
);

// SDK method: availabilityExceptions.create
export const addAvailabilityExceptionRequest = requestAction(ADD_EXCEPTION_REQUEST);
export const addAvailabilityExceptionSuccess = successAction(ADD_EXCEPTION_SUCCESS);
export const addAvailabilityExceptionError = errorAction(ADD_EXCEPTION_ERROR);

// SDK method: availabilityExceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(DELETE_EXCEPTION_REQUEST);
export const deleteAvailabilityExceptionSuccess = successAction(DELETE_EXCEPTION_SUCCESS);
export const deleteAvailabilityExceptionError = errorAction(DELETE_EXCEPTION_ERROR);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
export const savePayoutDetailsSuccess = successAction(SAVE_PAYOUT_DETAILS_SUCCESS);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

// Save Draft Listing
export const saveDraftListingRequest = requestAction(SAVE_DRAFT_LISTING_REQUEST);
export const saveDraftListingSuccess = successAction(SAVE_DRAFT_LISTING_SUCCESS);
export const saveDraftListingError = errorAction(SAVE_DRAFT_LISTING_ERROR);
export const clearSaveDraftListing = () => ({
  type: CLEAR_SAVE_DRAFT_LISTING,
});

export const showPendingApprovalNotification = () => ({
  type: SHOW_PENDING_APPROVAL_NOTIFICATION,
});

export const clearPendingApprovalNotification = () => ({
  type: CLEAR_PENDING_APPROVAL_NOTIFICATION,
});

// ================ Thunk ================ //

export function requestShowListing(actionPayload, config) {
  return (dispatch, getState, sdk) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      // protectedFileAttachments must be included or the response comes back without
      // them, leaving redux (and therefore the Files panel) unaware that the save worked.
      include: [
        'author',
        'images',
        'currentStock',
        'protectedFileAttachments',
        'protectedFileAttachments.file',
      ],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(showListingsRequest(actionPayload));
    return sdk.ownListings
      .show({ ...actionPayload, ...queryParams })
      .then(response => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

// Set stock if requested among listing update info
export function compareAndSetStock(listingId, oldTotal, newTotal) {
  return (dispatch, getState, sdk) => {
    dispatch(setStockRequest());

    return sdk.stock
      .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
      .then(response => {
        // NOTE: compareAndSet returns the stock resource of the listing.
        // We update client app's internal state with these updated API entities.
        dispatch(addMarketplaceEntities(response));
        dispatch(setStockSuccess(response));
      })
      .catch(e => {
        log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
        return dispatch(setStockError(storableError(e)));
      });
  };
}

// Helper function to make compareAndSetStock call if stock update is needed.
const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
export function requestCreateListingDraft(data, config, isSaveDraft) {
  return (dispatch, getState, sdk) => {
    dispatch(isSaveDraft ? saveDraftListingRequest() : createListingDraftRequest(data));
    const { stockUpdate, images, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    // Note: in this template, image upload is not happening at the same time as listing creation.
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingValues = { ...imageProperty, ...rest };

    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      // protectedFileAttachments must be included or the response comes back without
      // them, leaving redux (and therefore the Files panel) unaware that the save worked.
      include: [
        'author',
        'images',
        'currentStock',
        'protectedFileAttachments',
        'protectedFileAttachments.file',
      ],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    let createDraftResponse = null;
    return sdk.ownListings
      .createDraft(ownListingValues, queryParams)
      .then(response => {
        createDraftResponse = response;
        const listingId = response.data.data.id;
        // If stockUpdate info is passed through, update stock
        return updateStockOfListingMaybe(listingId, stockUpdate, dispatch);
      })
      .then(() => {
        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(createDraftResponse));
        return createDraftResponse;
      })
      .catch(e => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        const errorAction = isSaveDraft ? saveDraftListingError : createListingDraftError;
        return dispatch(errorAction(storableError(e)));
      });
  };
}

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
// NOTE: what comes to stock management, this follows the same pattern used in create listing call
// MODERATION: For published listings, changes are stored in metadata.pendingEdits instead of updating directly
export function requestUpdateListing(tab, data, config, isSaveDraft = false) {
  return (dispatch, getState, sdk) => {
    dispatch(isSaveDraft ? saveDraftListingRequest() : updateListingRequest(data));
    
    const { id, stockUpdate, images, ...rest } = data;
    const state = getState();
    
    // Check if listing is published (requires edit approval)
    // Look in both ownListing and listing entities to handle different loading paths
    const idString = id?.uuid || id;
    const ownListingEntities = state.marketplaceData.entities.ownListing || {};
    const listingEntities = state.marketplaceData.entities.listing || {};
    const existingListing = ownListingEntities[idString] || listingEntities[idString];
    const listingState = existingListing?.attributes?.state;
    const isPublished = listingState === 'published';

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    
    // For published listings, store changes in metadata.pendingEdits for approval
    // Only store simple fields that can be serialized in metadata
    const { availabilityPlan, price, ...simpleFields } = rest;
    // Convert UUID objects to plain strings - metadata must contain plain JSON-serializable values.
    // The Integration SDK's transit serializer won't recognize client-SDK UUID objects,
    // causing the listings.update call to fail when imageIds are included.
    const toUUIDString = id => id?.uuid || (typeof id === 'string' ? id : null);
    const pendingEditsData = {
      ...simpleFields,
      ...(images ? { imageIds: imageIds(images).map(toUUIDString).filter(Boolean) } : {}),
      ...(availabilityPlan ? { availabilityPlanJSON: JSON.stringify(availabilityPlan) } : {}),
      ...(price ? { priceJSON: JSON.stringify(price) } : {}),
    };
    
    // For published listings, we need to use server endpoint to update metadata
    // For draft/new listings, use normal SDK update
    const shouldUseServerEndpoint = isPublished;
    
    const ownListingUpdateValues = {
      id,
      ...imageProperty,
      ...rest
    };
    
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      // protectedFileAttachments must be included or the response comes back without
      // them, leaving redux (and therefore the Files panel) unaware that the save worked.
      include: [
        'author',
        'images',
        'currentStock',
        'protectedFileAttachments',
        'protectedFileAttachments.file',
      ],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    const existingTimeZone =
      existingListing?.attributes?.availabilityPlan?.timezone;
    const includedTimeZone = rest?.availabilityPlan?.timezone;

    // Note: if update values include stockUpdate, we'll do that first
    // That way we get updated currentStock info among ownListings.update
    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => {
        if (shouldUseServerEndpoint && !isSaveDraft) {
          // Published listing: use server endpoint to update metadata
          return submitListingEdit({
            listingId: id.uuid,
            pendingEdits: pendingEditsData,
            userId: state.user.currentUser?.id?.uuid,
          });
        } else {
          // Draft listing: use normal SDK update
          return sdk.ownListings.update(ownListingUpdateValues, queryParams);
        }
      })
      .then(response => {
        dispatch(updateListingSuccess(response));
        dispatch(addMarketplaceEntities(response));
        dispatch(
          isSaveDraft ? saveDraftListingSuccess({ data: { response, tab } }) : markTabUpdated(tab)
        );

        // If edits were stored for approval via Integration SDK (published listing),
        // the response images may lack proper variant keys (Integration SDK returns a different
        // variant format than the client SDK). Re-fetch the listing via the client SDK so
        // Redux entities have the correct image variants for rendering.
        if (isPublished && !isSaveDraft) {
          const listingId = response.data?.data?.id;
          if (listingId) {
            dispatch(requestShowListing({ id: listingId }, config));
          }
        }

        // If edits were stored for approval (published listing), notify admin
        if (isPublished && !isSaveDraft) {
          const listing = response.data.data;
          const currentUser = state.user.currentUser;
          const sellerName = currentUser?.attributes?.profile?.displayName || 'Seller';
          const sellerEmail = currentUser?.attributes?.email;
          
          // Show notification to user that changes are pending approval
          dispatch(showPendingApprovalNotification());
          
          // Auto-dismiss notification after 10 seconds
          setTimeout(() => {
            dispatch(clearPendingApprovalNotification());
          }, 10000);
          
          // Send notification email to admin (fire and forget, don't block on email failure)
          notifyAdminOnEditSubmitted({
            listingTitle: listing.attributes.title,
            listingId: listing.id.uuid,
            sellerName,
            sellerEmail,
          }).catch(emailError => {
            console.error('Failed to send admin notification email:', emailError);
          });
        }

        // If time zone has changed, we need to fetch exceptions again
        // since week and month boundaries might have changed.
        // Skip this check if edits are pending approval
        if (!isPublished && !!includedTimeZone && includedTimeZone !== existingTimeZone) {
          const searchString = '';
          const firstDayOfWeek = config.localization.firstDayOfWeek;
          const listing = response.data.data;
          fetchLoadDataExceptions(dispatch, listing, searchString, firstDayOfWeek);
        }

        return response;
      })
      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        const errorAction = isSaveDraft ? saveDraftListingError : updateListingError;
        return dispatch(errorAction(storableError(e)));
      })
      .finally(() => {
        if (isSaveDraft) {
          setTimeout(() => {
            dispatch(clearSaveDraftListing());
          }, 5000);
        }
      });
  };
}

export const requestPublishListingDraft = listingId => (dispatch, getState, sdk) => {
  dispatch(publishListingRequest(listingId));

  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true })
    .then(response => {
      // Add the created listing to the marketplace data
      dispatch(addMarketplaceEntities(response));
      dispatch(publishListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(publishListingError(storableError(e)));
    });
};

// Images return imageId which we need to map with previously generated temporary id
export function requestImageUpload(actionPayload, listingImageConfig) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    const imageVariantInfo = getImageVariantInfo(listingImageConfig);
    const queryParams = {
      expand: true,
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(uploadImageRequest(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file }, queryParams)
      .then(resp => {
        const img = resp.data.data;
        // Uploaded image has an existing id that refers to file
        // The UUID was created as a consequence of this upload call - it's saved to imageId property
        return dispatch(
          uploadImageSuccess({ data: { ...img, id, imageId: img.id, file: actionPayload.file } })
        );
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

// ================ Digital download file thunks ================ //

/**
 * Poll the API until an uploaded file finishes virus/format verification.
 *
 * The API accepts the bytes immediately but verifies asynchronously, so a file
 * is not usable the moment the upload call resolves. Polling backs off
 * exponentially (capped at a minute) and gives up after POLL_MAX_ATTEMPTS.
 */
export const pollForFileVerification = (fileId, tempId) => (dispatch, getState, sdk) => {
  dispatch(pollFileVerificationRequest({ tempId }));

  const poll = (attempt, delayMs) => {
    // The user may have removed the file while we were waiting - stop quietly.
    const stillTracked = getState().EditListingPage.fileUploads[tempId];
    if (!stillTracked) {
      return Promise.resolve({ tempId, sent: true });
    }

    return sdk.ownFiles.show({ id: fileId }).then(resp => {
      const fileState = resp?.data?.data?.attributes?.state;
      dispatch(setVerificationStatus({ tempId, verificationStatus: fileState }));

      if (fileState === 'available') {
        const [fileUpload] = denormalisedResponseEntities(resp);
        return dispatch(pollFileVerificationSuccess({ tempId, fileUpload }));
      }

      if (fileState === 'verificationFailed') {
        const [fileUpload] = denormalisedResponseEntities(resp);
        return dispatch(
          pollFileVerificationError({ tempId, message: 'verificationFailed', fileUpload })
        );
      }

      if (attempt >= POLL_MAX_ATTEMPTS - 1) {
        return dispatch(pollFileVerificationError({ tempId, message: 'timeout' }));
      }

      return delay(delayMs).then(() => poll(attempt + 1, getNextPollingDelay(delayMs)));
    });
  };

  return poll(0, POLL_INTERVAL_MS).catch(e =>
    dispatch(pollFileVerificationError({ tempId, error: storableError(e) }))
  );
};

/**
 * Run the multi-step file upload, then hand off to verification polling.
 * `tempId` is generated client-side so the UI can track a file before the API
 * has given it an id.
 */
export const uploadFile = (file, tempId) => (dispatch, getState, sdk) => {
  dispatch(uploadFileRequest({ tempId, file }));

  return executeFileUpload({
    file,
    tempId,
    sdk,
    fileUploadCount: Object.keys(getState().EditListingPage.fileUploads).length,
    onProgress: progress => dispatch(setUploadProgress({ tempId, progress })),
  })
    .then(resp => {
      dispatch(uploadFileSuccess({ tempId, fileUpload: resp.fileUpload }));
      dispatch(pollForFileVerification(resp.fileId, tempId));
      return resp;
    })
    .catch(e => dispatch(uploadFileError({ tempId, file, error: storableError(e) })));
};

/**
 * Exchange a file attachment id for a short-lived download URL and open it.
 */
export const downloadFile = fileAttachmentId => (dispatch, getState, sdk) => {
  if (!fileAttachmentId) {
    return Promise.reject(new Error('Missing fileAttachmentId, cannot initiate download.'));
  }
  dispatch(downloadFileRequest({ fileAttachmentId }));

  return sdk.ownFileDownloads
    .create({ fileId: fileAttachmentId })
    .then(downloadResp => {
      const { url } = downloadResp?.data?.data?.attributes || {};
      if (!url) {
        throw new Error('Missing download URL, cannot trigger file download.');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      dispatch(downloadFileSuccess({ fileAttachmentId }));
      return fileAttachmentId;
    })
    .catch(e => {
      dispatch(downloadFileError({ fileAttachmentId, error: storableError(e) }));
      throw e;
    });
};

export const requestAddAvailabilityException = params => (dispatch, getState, sdk) => {
  dispatch(addAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .create(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return dispatch(addAvailabilityExceptionSuccess({ data: availabilityException }));
    })
    .catch(e => {
      dispatch(addAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestDeleteAvailabilityException = params => (dispatch, getState, sdk) => {
  dispatch(deleteAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .delete(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return dispatch(deleteAvailabilityExceptionSuccess({ data: availabilityException }));
    })
    .catch(e => {
      dispatch(deleteAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestFetchAvailabilityExceptions = params => (dispatch, getState, sdk) => {
  const { listingId, start, end, timeZone, page, isWeekly } = params;
  const fetchParams = { listingId, start, end };
  const timeUnitIdProp = isWeekly
    ? { weekStartId: stringifyDateToISO8601(start) }
    : { monthId: monthIdString(start, timeZone) };
  dispatch(fetchAvailabilityExceptionsRequest(timeUnitIdProp));

  return sdk.availabilityExceptions
    .query(fetchParams)
    .then(response => {
      const availabilityExceptions = denormalisedResponseEntities(response);

      // Fetch potential extra exceptions pagination pages per month.
      // In theory, there could be several pagination pages worth of exceptions,
      // if range is month and unit is 'hour': 31 days * 24 hour = 744 slots for exceptions.
      const totalPages = response.data.meta.totalPages;
      if (totalPages > 1 && !page) {
        const extraPages = getArrayOfNItems(totalPages);

        // It's unlikely that this code is reached with default units.
        // Note:
        //  - Firing multiple API calls might hit API rate limit
        //    (This is very unlikely with this query and 'hour' unit.)
        //  - TODO: this doesn't take care of failures of those extra calls
        Promise.all(
          extraPages.map(page => {
            return sdk.availabilityExceptions.query({ ...fetchParams, page });
          })
        ).then(responses => {
          const denormalizedFlatResults = (all, r) => all.concat(denormalisedResponseEntities(r));
          const exceptions = responses.reduce(denormalizedFlatResults, []);
          dispatch(
            fetchExtraAvailabilityExceptionsSuccess({
              data: { ...timeUnitIdProp, exceptions },
            })
          );
        });
      }

      return dispatch(
        fetchAvailabilityExceptionsSuccess({
          data: { ...timeUnitIdProp, exceptions: availabilityExceptions },
        })
      );
    })
    .catch(e => {
      return dispatch(
        fetchAvailabilityExceptionsError({ ...timeUnitIdProp, error: storableError(e) })
      );
    });
};

// Helper function for loadData call.
const fetchLoadDataExceptions = (dispatch, listing, search, firstDayOfWeek) => {
  const hasWindow = typeof window !== 'undefined';
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  // Note: listing needs to have time zone set!
  if (hasWindow && listing.id && hasTimeZone) {
    const listingId = listing.id;
    // If the listing doesn't have availabilityPlan yet
    // use the defaul timezone
    const timezone = listing.attributes.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
    const todayInListingsTZ = getStartOf(new Date(), 'day', timezone);

    const locationSearch = parse(search);
    const selectedDate = locationSearch?.d
      ? parseDateFromISO8601(locationSearch.d, timezone)
      : todayInListingsTZ;
    const startOfWeek = getStartOfWeek(selectedDate, timezone, firstDayOfWeek);
    const prevWeek = getStartOf(startOfWeek, 'day', timezone, -7, 'days');
    const nextWeek = getStartOf(startOfWeek, 'day', timezone, 7, 'days');
    const nextAfterNextWeek = getStartOf(nextWeek, 'day', timezone, 7, 'days');

    const nextMonth = getStartOf(todayInListingsTZ, 'month', timezone, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', timezone, 1, 'months');

    const sharedData = { listingId, timeZone: timezone };

    // Fetch data for selected week and nearest weeks for WeeklyCalendar
    // Plus current month and month after that for EditListingAvailabilityForm
    //
    // NOTE: This is making 5 different Thunk calls, which update store 2 times each
    //       It would make sense to make on thunk function that fires 5 sdk calls/promises,
    //       but for the time being, it's clearer to push all the calls through
    //       requestFetchAvailabilityExceptions
    return Promise.all([
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: prevWeek,
          end: startOfWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: startOfWeek,
          end: nextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: nextWeek,
          end: nextAfterNextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: todayInListingsTZ,
          end: nextMonth,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: nextMonth,
          end: nextAfterNextMonth,
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  dispatch(clearPublishError());
  const { id, type } = params;
  const fetchCurrentUserOptions = {
    updateNotifications: false,
  };

  if (type === 'new') {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser(fetchCurrentUserOptions))])
      .then(response => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  const payload = { id: new UUID(id) };
  return Promise.all([
    dispatch(requestShowListing(payload, config)),
    dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
  ])
    .then(response => {
      const currentUser = getState().user.currentUser;

      // Do not fetch extra information if user is in pending-approval state.
      if (isUserAuthorized(currentUser)) {
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }

        // Because of two dispatch functions, response is an array.
        // We are only interested in the response from requestShowListing here,
        // so we need to pick the first one
        const listing = response[0]?.data?.data;
        const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
        if (listing && isBookingProcessAlias(transactionProcessAlias)) {
          fetchLoadDataExceptions(dispatch, listing, search, config.localization.firstDayOfWeek);
        }
      }

      return response;
    })
    .catch(e => {
      throw e;
    });
};
